import React, { createContext, useContext, useState } from 'react';

export type SelectedService = {
  serviceId: number;
  serviceName: string;
  categoryName?: string | null; 
  price: number;
  duration_minutes: number;
  color_class?: string | null;
  staffId?: number | null;
  staffName?: string | null;
};

export type ReserveState = {
  storeId: number | null;
  storeName: string | null;
  customerId: number | null;
  skippedStoreSelectionByDate: boolean;
  skippedStoreSelectionByService: boolean;
  skippedStoreSelectionByStaff: boolean;
  selectedServices: SelectedService[];
  selectedDate: string | null;       // 'YYYY-MM-DD'
  selectedStartTime: string | null;  // 'HH:mm'
  selectedStaffId: number | null;    // ★ 追加
  selectedStaffName: string | null;  // ★ 追加
  isFreeReservation: boolean;
};

export type ReserveContextType = ReserveState & {
  setStoreId: (id: number) => void;
  setStoreName: (name: string | null) => void;
  setCustomerId: (id: number | null) => void;
  setSkippedStoreSelectionByDate: (value: boolean) => void;
  setSkippedStoreSelectionByService: (value: boolean) => void;
  setSkippedStoreSelectionByStaff: (value: boolean) => void;
  setSelectedServices: (services: SelectedService[]) => void;
  setSelectedDate: (date: string | null) => void;
  setSelectedStartTime: (time: string | null) => void;
  setSelectedStaffId: (id: number | null) => void;      // ★ 追加
  setSelectedStaffName: (name: string | null) => void;  // ★ 追加
  setIsFreeReservation: (value: boolean) => void;
};

const ReserveContext = createContext<ReserveContextType | undefined>(undefined);

export const ReserveProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [storeId, setStoreId] = useState<number | null>(null);
  const [storeName, setStoreName] = useState<string | null>(null);
  const [customerId, setCustomerId] = useState<number | null>(null);
  const [skippedStoreSelectionByDate, setSkippedStoreSelectionByDate] = useState(false);
  const [skippedStoreSelectionByService, setSkippedStoreSelectionByService] = useState(false);
  const [skippedStoreSelectionByStaff, setSkippedStoreSelectionByStaff] = useState(false);
  const [selectedServices, setSelectedServices] = useState<SelectedService[]>([]);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(null);
  const [selectedStaffId, setSelectedStaffId] = useState<number | null>(null);
  const [selectedStaffName, setSelectedStaffName] = useState<string | null>(null);
  const [isFreeReservation, setIsFreeReservation] = useState(false);

  return (
    <ReserveContext.Provider
      value={{
        storeId,
        storeName,
        customerId,
        skippedStoreSelectionByDate,
        skippedStoreSelectionByService,
        skippedStoreSelectionByStaff,
        selectedServices,
        selectedDate,
        selectedStartTime,
        selectedStaffId,
        selectedStaffName,
        isFreeReservation,
        setStoreId,
        setStoreName,
        setCustomerId,
        setSkippedStoreSelectionByDate,
        setSkippedStoreSelectionByService,
        setSkippedStoreSelectionByStaff,
        setSelectedServices,
        setSelectedDate,
        setSelectedStartTime,
        setSelectedStaffId,
        setSelectedStaffName,
        setIsFreeReservation,
      }}
    >
      {children}
    </ReserveContext.Provider>
  );
};

export const useReserveContext = () => {
  const context = useContext(ReserveContext);
  if (!context) {
    throw new Error('useReserveContext must be used within a ReserveProvider');
  }
  return context;
};
